import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card } from "../../components/ui/Card";

const docModules = import.meta.glob("../../docs/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

interface DocEntry {
  file: string;
  title: string;
  content: string;
}

function toTitle(file: string): string {
  const base = file.replace(/^.*\//, "").replace(/\.md$/i, "");
  return base.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function SuperadminDocsPage() {
  const docs = useMemo<DocEntry[]>(() => {
    return Object.entries(docModules)
      .map(([path, content]) => ({
        file: path.replace(/^.*\//, ""),
        title: toTitle(path),
        content,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, []);

  const [selected, setSelected] = useState<string>("");

  if (docs.length === 0) {
    return (
      <Card title="Docs">
        <p className="muted">
          No docs yet. Drop <code>.md</code> files into{" "}
          <code>apps/web/src/docs/</code> and they'll show up here.
        </p>
      </Card>
    );
  }

  const current = docs.find((d) => d.file === selected) ?? null;

  return (
    <Card
      title="Docs"
      subtitle={`${docs.length} document${docs.length === 1 ? "" : "s"} bundled with the app.`}
    >
      <div className="form-stack">
        <label className="field">
          <span className="field-label">Document</span>
          <select
            className="field-input"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">— Select a document —</option>
            {docs.map((d) => (
              <option key={d.file} value={d.file}>
                {d.title} ({d.file})
              </option>
            ))}
          </select>
        </label>
        {current ? (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.content}</ReactMarkdown>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
